import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Trash2, LogOut, ShieldCheck, Star, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

const Stars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={14} className={n <= value ? "text-terracotta fill-terracotta" : "text-line"} />
    ))}
  </div>
);

const ReviewRow = ({ r, onApprove, onDelete }) => (
  <div data-testid={`admin-review-${r.id}`} className="border border-line bg-surface p-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
    <div className="flex-1">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-heading font-bold uppercase text-sm tracking-[0.15em] text-bone">{r.name}</span>
        {r.location ? <span className="font-body text-stone text-xs">· {r.location}</span> : null}
      </div>
      <Stars value={r.rating} />
      <p className="font-body font-light text-stone mt-3 max-w-2xl">"{r.quote}"</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {r.status === "pending" && (
        <button
          data-testid={`approve-${r.id}`}
          onClick={() => onApprove(r.id)}
          className="inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.15em] font-bold bg-terracotta text-bone px-4 py-3 hover:opacity-90 transition-opacity"
        >
          <Check size={14} /> Approve
        </button>
      )}
      <button
        data-testid={`delete-${r.id}`}
        onClick={() => onDelete(r.id)}
        className="inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.15em] font-bold border border-line text-stone px-4 py-3 hover:text-bone hover:border-bone transition-colors"
      >
        <Trash2 size={14} /> {r.status === "pending" ? "Reject" : "Remove"}
      </button>
    </div>
  </div>
);

export default function Admin() {
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | login | denied | ready
  const [user, setUser] = useState(location.state?.user || null);
  const [reviews, setReviews] = useState([]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, cfg);
      setReviews(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const me = user || (await axios.get(`${API}/auth/me`, cfg)).data;
      setUser(me);
      if (me.is_admin) {
        setStatus("ready");
        loadReviews();
      } else {
        setStatus("denied");
      }
    } catch (e) {
      setStatus("login");
    }
  }, [user, loadReviews]);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, {}, cfg);
      toast.success("Review approved — now live on the site.");
      loadReviews();
    } catch (e) {
      toast.error("Could not approve.");
    }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, cfg);
      toast.success("Review removed.");
      loadReviews();
    } catch (e) {
      toast.error("Could not remove.");
    }
  };
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, cfg);
    } catch (e) {}
    window.location.href = "/admin";
  };

  const pending = reviews.filter((r) => r.status === "pending");
  const approved = reviews.filter((r) => r.status === "approved");

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <p className="font-heading uppercase tracking-[0.3em] text-stone text-sm animate-pulse">Loading…</p>
      </div>
    );
  }

  if (status === "login" || status === "denied") {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6">
        <div className="border border-line bg-surface p-10 md:p-14 max-w-md w-full text-center">
          <img src="/media/mg-mark.png" alt="MG Tiling Pro" className="h-12 w-auto mx-auto mb-8" />
          <h1 className="font-heading text-3xl font-extrabold uppercase text-bone tracking-tight mb-3">
            Admin Access
          </h1>
          {status === "denied" ? (
            <>
              <p className="font-body font-light text-stone mb-8">
                This Google account isn't authorised to manage reviews.
              </p>
              <button
                data-testid="admin-logout"
                onClick={logout}
                className="inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.2em] font-bold border border-line text-bone px-8 py-4 hover:border-bone transition-colors"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          ) : (
            <>
              <p className="font-body font-light text-stone mb-8">
                Sign in with your Google account to approve customer reviews.
              </p>
              <button
                data-testid="google-login"
                onClick={login}
                className="w-full font-heading text-xs uppercase tracking-[0.2em] font-bold bg-bone text-ink px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors"
              >
                Sign in with Google
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-line">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/media/mg-mark.png" alt="MG" className="h-9 w-auto" />
            <span className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-stone hidden sm:inline">
              Reviews Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-stone text-sm hidden md:inline">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.15em] font-bold border border-line text-stone px-4 py-3 hover:text-bone hover:border-bone transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-14 space-y-16">
        <section data-testid="pending-section">
          <div className="flex items-center gap-3 mb-6">
            <Clock size={18} className="text-terracotta" />
            <h2 className="font-heading text-2xl font-extrabold uppercase text-bone tracking-tight">
              Pending ({pending.length})
            </h2>
          </div>
          {pending.length === 0 ? (
            <p className="font-body font-light text-stone">No reviews waiting. You're all caught up.</p>
          ) : (
            <div className="space-y-4">
              {pending.map((r) => (
                <ReviewRow key={r.id} r={r} onApprove={approve} onDelete={remove} />
              ))}
            </div>
          )}
        </section>

        <section data-testid="approved-section">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck size={18} className="text-terracotta" />
            <h2 className="font-heading text-2xl font-extrabold uppercase text-bone tracking-tight">
              Live on site ({approved.length})
            </h2>
          </div>
          {approved.length === 0 ? (
            <p className="font-body font-light text-stone">No approved reviews yet.</p>
          ) : (
            <div className="space-y-4">
              {approved.map((r) => (
                <ReviewRow key={r.id} r={r} onApprove={approve} onDelete={remove} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
